import { findIntersectionsBetweenArrays, findIntersectionsBetweenSets } from '@anecdotes/utils';
import { Dictionary } from '@ngrx/entity';
import { FieldsState, FieldState, FilterState, OptionsState, OptionState } from '../../../models';

export class FilterInstanceHelper {
  static Instance = new FilterInstanceHelper();

  isEmptyState(previousState: FilterState, newFieldsState: FieldsState): boolean {
    if (!previousState.fieldsState) {
      return false;
    }

    let isEmptyState = Object.values(previousState.fieldsState).some((field) => {
      const checkedOptions = Object.values(field.options).filter((option) => option.isChecked);

      if (!checkedOptions.length) {
        return false;
      }

      return !checkedOptions.some((option) => option.id in newFieldsState[field.id].options);
    });

    return isEmptyState;
  }

  resetFields(fieldStates: FieldState[], currentState: FilterState): FilterState {
    const newFieldsState: FieldsState = { ...currentState.fieldsState };

    fieldStates.forEach((fieldState) => {
      newFieldsState[fieldState.id] = {
        ...fieldState,
      };

      Object.values(fieldState.options).forEach((optionState) => {
        if (!newFieldsState[fieldState.id].ignoreForReset) {
          newFieldsState[fieldState.id].options[optionState.id] = {
            ...optionState,
            isChecked: optionState.useCheckedByDefaultAfterReset && optionState.isCheckedByDefault,
          };
        }
      });
    });

    this.refreshOptions(newFieldsState);

    return {
      ...currentState,
      filteredDataIds: this.calculateFilteredDataIdsForFields(
        Object.values(newFieldsState),
        currentState.dataToFilter,
        true
      ),
      fieldsState: newFieldsState,
      isEmptyState: false,
    };
  }

  changeSelectionState(
    optionsData: { [fieldId: string]: string[] },
    state: FilterState,
    isSelected: boolean
  ): FilterState {
    const newOptionsData = Object.keys(optionsData).reduce((result, fieldId) => {
      const optionsToSelect = optionsData[fieldId];
      const field = state.fieldsState[fieldId];

      return {
        ...result,
        [fieldId]: optionsToSelect.filter((optionId) => !!field.options[optionId]?.isChecked !== isSelected),
        isEmptyState: false,
      };
    }, {});

    return this.toggleOptionsInState(newOptionsData, state);
  }

  setOptionsInState(
    optionsData: { [fieldId: string]: string[] },
    state: FilterState,
  ): FilterState {
    const newFieldsState = {
      ...state.fieldsState,
      ...this.setOptions(optionsData, state.fieldsState),
    };

    this.refreshOptions(newFieldsState);

    return {
      ...state,
      filteredDataIds: this.calculateFilteredDataIdsForFields(Object.values(newFieldsState), state.dataToFilter, true),
      fieldsState: newFieldsState,
      isEmptyState: false,
    };
  }

  private setOptions(optionsData: { [fieldId: string]: string[] }, fieldsState: FieldsState): FieldsState {
    return Object.keys(optionsData).reduce((result, fieldId) => {
      if (!fieldsState[fieldId]) {
        return result;
      }

      const newFieldDefState: FieldState = { ...fieldsState[fieldId] };
      newFieldDefState.options = { ...newFieldDefState.options };

      const optionsArr = optionsData[fieldId];
      const optionsToSelect = new Set(newFieldDefState.isSingleSelection ? optionsArr.slice(-1) : optionsArr);

      Object.keys(newFieldDefState.options).forEach((optionId) => {
        newFieldDefState.options[optionId] = {
          ...newFieldDefState.options[optionId],
          isChecked: optionsToSelect.has(optionId),
        };
      });

      result[fieldId] = newFieldDefState;

      return result;
    }, {});
  }

  toggleOptions(optionsData: { [fieldId: string]: string[] }, fieldsState: FieldsState): FieldsState {
    const newFieldStates = Object.keys(optionsData).reduce((result, fieldId) => {
      if (!fieldsState[fieldId]) {
        return result;
      }

      const currentField = fieldsState[fieldId];
      const optionsToToggle = optionsData[fieldId].filter((optionId) => optionId in currentField.options);
      const newFieldDefState: FieldState = { ...fieldsState[fieldId] };
      newFieldDefState.options = { ...newFieldDefState.options };

      if (newFieldDefState.isSingleSelection) {
        if (optionsToToggle.length) {
          const lastOptionId = optionsToToggle[optionsToToggle.length - 1];

          Object.keys(newFieldDefState.options).forEach((optionId) => {
            if (optionId === lastOptionId && !newFieldDefState.isSwitcher) {
              newFieldDefState.options[optionId] = { ...newFieldDefState.options[optionId], isChecked: newFieldDefState.options[optionId]?.isChecked };
            } else {
              newFieldDefState.options[optionId] = { ...newFieldDefState.options[optionId], isChecked: false };
            }
          });
          newFieldDefState.options[lastOptionId] = {
            ...newFieldDefState.options[lastOptionId],
            isChecked: !newFieldDefState.options[lastOptionId]?.isChecked,
          };
        }
      } else {
        optionsToToggle.forEach((optionId) => {
          newFieldDefState.options[optionId] = {
            ...newFieldDefState.options[optionId],
            isChecked: !newFieldDefState.options[optionId]?.isChecked,
          };
        });
      }
      return { ...result, [fieldId]: newFieldDefState };
    }, {});

    return { ...fieldsState, ...newFieldStates };
  }

  toggleOptionsInState(
    optionsData: { [fieldId: string]: string[] },
    state: FilterState
  ): FilterState {
    const newFieldsState = this.toggleOptions(optionsData, state.fieldsState);

    this.refreshOptions(newFieldsState);

    return {
      ...state,
      filteredDataIds: this.calculateFilteredDataIdsForFields(Object.values(newFieldsState), state.dataToFilter, true),
      fieldsState: newFieldsState,
      isEmptyState: false,
    };
  }

  mergeDefinitions(currentDefinition: FieldsState, previousDefinition: FieldsState): FieldsState {
    if (!previousDefinition) {
      return currentDefinition;
    }

    const mergedDefinition = Object.keys(currentDefinition).reduce((result, fieldId) => {
      result[fieldId] = { ...currentDefinition[fieldId] };
      const newField: FieldState = result[fieldId];
      const prevField: FieldState = previousDefinition[fieldId];

      // When option’s counter changes to 0 (while it is checked) -
      // the method compares the prev and the new states to find what became 0
      // and adds it manually to the filters in order to be able and display the empty state
      Object.values(prevField?.options || []).forEach((prevOption) => {
        const newOption = newField.options[prevOption.id];

        if (!newOption && prevOption.isChecked) {
          // In the case when previous option was previously checked and it no longer present in the new field state
          // marks option for removing after the next user interaction with the filter.
          newField.options[prevOption.id] = { ...prevOption, relatedDataInfo: {} };
        }
      });

      if (prevField) {
        newField.options = Object.keys(newField.options).reduce((optionsResult, optionId) => {
          optionsResult[optionId] = { ...newField.options[optionId] };
          const newOption = optionsResult[optionId];
          const prevOption = prevField.options[optionId];

          if (prevOption) {
            newOption.isChecked = prevOption.isChecked;
          }

          return optionsResult;
        }, {} as OptionsState);
      }

      return result;
    }, {} as FieldsState);

    return mergedDefinition;
  }

  /**
   * Removes options that are marked for removing (option.pendingRemove === true)
   * Unchecks options that does not have related data
   * @param fieldsState fields state to make changes in
   */
  refreshOptions(fieldsState: FieldsState): void {
    Object.values(fieldsState).forEach((fieldState) => {
      Object.values(fieldState.options).forEach((option: OptionState) => {
        if (!option.isChecked && !option.isFixed && !Object.keys(option.relatedDataInfo ?? {}).length) {
          // Remove the option if the the option does not have related data and uncheked and option doesn't fixed
          delete fieldState.options[option.id];
        }
      });
    });
  }

  handleDataAndDefinitionChange(
    state: FilterState,
    filterDefinitionState: FieldsState,
    dataToFilter: Dictionary<any>
  ): FilterState {
    let filteredDataIds = state.filteredDataIds;
    let areFilteredDataCalculationPending = !state.isInitialized;

    let shouldCalculationRun =
      areFilteredDataCalculationPending && filterDefinitionState && dataToFilter && Object.keys(dataToFilter).length;

    if (shouldCalculationRun) {
      filteredDataIds = this.calculateFilteredDataIdsForFields(
        Object.values(filterDefinitionState),
        dataToFilter,
        true
      );
      areFilteredDataCalculationPending = undefined;
    }

    return {
      ...state,
      dataToFilter,
      filteredDataIds,
      fieldsState: filterDefinitionState,
      isInitialized: true,
    };
  }

  calculateFilteredDataIdsForFields(
    fields: FieldState[],
    dataToFilter: Dictionary<any>,
    returnAllWhenNoCheckedOptions: boolean
  ): string[] {
    const allFieldsWithSelectedOptions = fields
      .map((field) => Object.values(field.options).filter((option) => option.isChecked))
      .filter((options) => options.length);

    if (!allFieldsWithSelectedOptions.length && returnAllWhenNoCheckedOptions) {
      return Object.keys(dataToFilter);
    }

    const optionsWithReverseFilter = fields
      .map((field) => Object.values(field.options).filter((option) => !option.isChecked && option.reverseFilter))
      .filter((options) => options.length);

    const resolvedIdsWithReverseFilter: string[] = this.calculateFilteredDataIdsForOptionsGroups(
      optionsWithReverseFilter,
      dataToFilter
    );
    let resolvedIds: string[] = this.calculateFilteredDataIdsForOptionsGroups(
      allFieldsWithSelectedOptions,
      dataToFilter
    );

    if (resolvedIdsWithReverseFilter.length) {
      resolvedIdsWithReverseFilter.map((itemToExclude) => {
        resolvedIds = resolvedIds.filter((id) => id !== itemToExclude);
      });
    }

    const resolvedIdsSetByNestedData: Set<string> = this.resolveParentIdsByNestedData(fields);

    if (resolvedIdsSetByNestedData) {
      return resolvedIds.filter((resolvedId) => resolvedIdsSetByNestedData.has(resolvedId));
    } else {
      return resolvedIds;
    }
  }

  calculateFilteredDataIdsForOptionsGroups(options: OptionState[][], dataToFilter: Dictionary<any>): string[] {
    return findIntersectionsBetweenArrays(
      options.map((option) =>
        option.filter((opt) => opt && opt.relatedDataInfo).flatMap((opt) => Object.keys(opt.relatedDataInfo))
      )
    ).filter((id) => id in dataToFilter);
  }

  calculateCountForOptionInNestedGroup(
    fieldId: string,
    optionId: string,
    filteredParentDataIds: string[],
    filterInstanceState: FilterState
  ): number {
    const currentField: FieldState = filterInstanceState.fieldsState[fieldId];
    const currentOption: OptionState = currentField.options[optionId];

    const filteredParentDataIdsSet = new Set<string>(filteredParentDataIds);
    const fields = Object.values(filterInstanceState.fieldsState).filter(
      (field) => field.nestedGroupId === currentField.nestedGroupId && field.id !== fieldId
    );

    const nestedIdSetsForFields: Set<string>[] = [];

    // if there is no checked options we can just return count of related nested data
    if (!fields.some((field) => Object.values(field.options).some((option) => option.isChecked))) {
      return Object.keys(currentOption.relatedDataInfo).reduce((result, key) => {
        if (filteredParentDataIdsSet.has(key)) {
          currentOption.relatedDataInfo[key].nestedIds.forEach((nestedId) => {
            result.add(nestedId);
          });
        }

        return result;
      }, new Set<string>()).size;
    }

    fields.forEach((field) => {
      const fieldRelatedNestedIdSet = new Set<string>();

      const allOptions = Object.values(field.options);
      const checkedOptions = allOptions.filter((option) => option.isChecked);
      let optionsToCheck: OptionState[];

      if (checkedOptions.length) {
        optionsToCheck = checkedOptions;
      } else {
        optionsToCheck = allOptions;
      }

      optionsToCheck.forEach((option) => {
        Object.keys(option.relatedDataInfo)
          .filter((parentId) => filteredParentDataIdsSet.has(parentId))
          .forEach((parentId) => {
            option.relatedDataInfo[parentId].nestedIds.forEach((nestedId) => {
              fieldRelatedNestedIdSet.add(nestedId);
            });
          });
      });

      nestedIdSetsForFields.push(fieldRelatedNestedIdSet);
    });

    const intersections = findIntersectionsBetweenSets(nestedIdSetsForFields);

    const potentialNestedIdsSetForCurrentOption = Object.values(currentOption.relatedDataInfo).reduce(
      (result, relatedData) => {
        relatedData.nestedIds.forEach((nestedId) => {
          if (intersections.has(nestedId)) {
            result.add(nestedId);
          }
        });
        return result;
      },
      new Set<string>()
    );

    return potentialNestedIdsSetForCurrentOption.size;
  }

  calculateCountForNestedOption(
    fieldId: string,
    optionId: string,
    filterInstanceState: FilterState
  ): number {
    const currentField: FieldState = filterInstanceState.fieldsState[fieldId];
    const currentOption: OptionState = currentField.options[optionId];

    const set = filterInstanceState.filteredDataIds
      .filter((id) => id in currentOption.relatedDataInfo)
      .flatMap((id) => currentOption.relatedDataInfo[id].nestedIds);
    return new Set(set).size;
  }

  resolveParentIdsByNestedData(fields: FieldState[]): Set<string> {
    const groupedFieldsByNestedGroupId = fields
      .filter((field) => field.nestedGroupId)
      .reduce((result, curr) => {
        if (!(curr.nestedGroupId in result)) {
          result[curr.nestedGroupId] = [];
        }

        result[curr.nestedGroupId].push(curr);

        return result;
      }, {} as { [key: string]: FieldState[] });

    const groupsArray = Object.values(groupedFieldsByNestedGroupId);

    const composedInfoFromAllGroups: any[] = groupsArray.map((fields) => {
      const checkedOptionsFromFields: OptionState[][] = fields
        .map((field) => {
          const checked = Object.values(field.options).filter((option) => option.isChecked);

          if (checked.length) {
            return checked;
          }

          return null;
        })
        .filter((options) => options);

      const nestedAndParentDictionaries = [];

      checkedOptionsFromFields.forEach((options) => {
        const res = {};
        Object.values(options).forEach((opt) => {
          Object.keys(opt.relatedDataInfo).forEach((parentId) => {
            opt.relatedDataInfo[parentId].nestedIds.forEach((nestedId) => {
              if (!(nestedId in res)) {
                res[nestedId] = new Set<string>();
              }

              res[nestedId].add(parentId);
            });
          });
        });

        nestedAndParentDictionaries.push(res);
      });

      if (!nestedAndParentDictionaries.length) {
        return null;
      }

      const dictionaryOfAllNestedData = nestedAndParentDictionaries.reduce(
        (result, curr) => ({
          ...result,
          ...curr,
        }),
        {}
      );

      const setOfResolvedParents = findIntersectionsBetweenArrays(
        nestedAndParentDictionaries.map((nestedParentDictionary) => Object.keys(nestedParentDictionary))
      )
        .map((nestedId) => dictionaryOfAllNestedData[nestedId])
        .reduce((result, curr) => {
          curr.forEach((item) => {
            result.add(item);
          });

          return result;
        }, new Set<string>());

      return setOfResolvedParents;
    });

    if (composedInfoFromAllGroups.some(Boolean)) {
      return findIntersectionsBetweenSets(composedInfoFromAllGroups.filter(Boolean));
    }

    return null;
  }

  applyOptionsFromQueryParams(queryParams: { [name: string]: any }, fieldsState: FieldsState): FieldsState {
    const queryParamsFilterInfo = Object.keys(queryParams).filter((key) => queryParams[key]).reduce((result, currentQueryParamKey) => {
      let valuesArray: string[];
      const queryParamValue = queryParams[currentQueryParamKey];

      if (Array.isArray(queryParamValue)) {
        valuesArray = queryParamValue;
      } else if (typeof queryParamValue === 'string') {
        valuesArray = queryParamValue.split(',');
      }

      if (valuesArray) {
        result[currentQueryParamKey] = new Set(valuesArray.map((value) => {
          try {
            return decodeURIComponent(value);
          } catch {
            return value;
          }
        }));
      }

      return result;
    }, {});

    const optionData = Object.keys(queryParamsFilterInfo).reduce((result, fieldId) => {
      result[fieldId] = Array.from(queryParamsFilterInfo[fieldId]);
      return result;
    }, {});

    return {
      ...fieldsState,
      ...this.setOptions(optionData, fieldsState),
    };
  }
}
